import { createSlice } from '@reduxjs/toolkit';
import {
  IbanVerificationBulkDetails,
  IbanVerificationDetails,
  IbanVerificationRecord,
  IbanVerificationResponsePayload,
  PaginatedResponse,
  VerificationResponse,
} from '../avs.types';
import {
  verifyAccount,
  uploadBulkVerification,
  getVerificationRecords,
  getBulkVerificationResult,
  getBulkVerificationResultData,
  getIbanVerificationResult,
} from './avsIbanVerification.actions';

export interface AvsIbanVerificationState {
  verificationState: VerificationResponse<IbanVerificationResponsePayload> | null;
  verificationResultDetail: IbanVerificationDetails | null;
  verificationRecords: PaginatedResponse<IbanVerificationRecord> | null;
  bulkVerificationResponse: VerificationResponse<never> | null;
  bulkVerificationResult: IbanVerificationBulkDetails | null;
  bulkVerificationResultData: PaginatedResponse<IbanVerificationDetails> | null;
  isVerifying: boolean;
  isFetchingVerificationRecords: boolean;
  isFetchingVerificationResult: boolean;
  isFetchingVerificationResultData: boolean;
}

const initialState: AvsIbanVerificationState = {
  verificationState: null,
  verificationResultDetail: null,
  verificationRecords: null,
  bulkVerificationResponse: null,
  bulkVerificationResult: null,
  bulkVerificationResultData: null,
  isVerifying: false,
  isFetchingVerificationRecords: false,
  isFetchingVerificationResult: false,
  isFetchingVerificationResultData: false,
};

const avsIbanVerificationSlice = createSlice({
  name: 'avsIbanVerification',
  initialState,
  reducers: {
    resetVerificationState: state => {
      state.verificationState = null;
      state.isVerifying = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(verifyAccount.pending, state => {
      state.isVerifying = true;
    });
    builder.addCase(verifyAccount.fulfilled, (state, { payload }) => {
      state.isVerifying = false;
      state.verificationState = payload;
    });
    builder.addCase(verifyAccount.rejected, (state, { payload }) => {
      state.isVerifying = false;
      state.verificationState = payload as VerificationResponse<any>;
    });

    builder.addCase(uploadBulkVerification.pending, state => {
      state.bulkVerificationResponse = null;
      state.isFetchingVerificationResult = true;
    });
    builder.addCase(uploadBulkVerification.fulfilled, (state, { payload }) => {
      state.bulkVerificationResponse =
        payload as unknown as VerificationResponse<never>;
      state.isFetchingVerificationResult = false;
    });
    builder.addCase(uploadBulkVerification.rejected, (state, { payload }) => {
      state.bulkVerificationResponse = payload as VerificationResponse<never>;
      state.isFetchingVerificationResult = false;
    });

    builder.addCase(getVerificationRecords.pending, state => {
      state.isFetchingVerificationRecords = true;
    });
    builder.addCase(getVerificationRecords.fulfilled, (state, { payload }) => {
      state.isFetchingVerificationRecords = false;
      state.verificationRecords = payload;
    });
    builder.addCase(getVerificationRecords.rejected, state => {
      state.isFetchingVerificationRecords = false;
    });

    builder.addCase(getBulkVerificationResult.pending, state => {
      state.isFetchingVerificationResult = true;
    });
    builder.addCase(
      getBulkVerificationResult.fulfilled,
      (state, { payload }) => {
        state.isFetchingVerificationResult = false;
        state.bulkVerificationResult = payload;
      },
    );
    builder.addCase(getBulkVerificationResult.rejected, state => {
      state.isFetchingVerificationResult = false;
    });

    builder.addCase(
      getBulkVerificationResultData.pending,
      (state, { meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingVerificationResultData = true;
        }
      },
    );
    builder.addCase(
      getBulkVerificationResultData.fulfilled,
      (state, { payload, meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingVerificationResultData = false;
          state.bulkVerificationResultData = payload;
        }
      },
    );
    builder.addCase(getBulkVerificationResultData.rejected, state => {
      state.isFetchingVerificationResultData = false;
    });

    builder.addCase(getIbanVerificationResult.pending, (state, { meta }) => {
      if (!meta.arg.isCsvFile) {
        state.isFetchingVerificationResult = true;
      }
    });
    builder.addCase(
      getIbanVerificationResult.fulfilled,
      (state, { payload, meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingVerificationResult = false;
          state.verificationResultDetail = payload;
        }
      },
    );
    builder.addCase(getIbanVerificationResult.rejected, state => {
      state.isFetchingVerificationResult = false;
    });
  },
});

export default avsIbanVerificationSlice.reducer;
export const { resetVerificationState } = avsIbanVerificationSlice.actions;
